import React, { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useNavigationStore } from '../../stores/navigationStore';

/**
 * Invisible component that syncs route/query-param context into the
 * navigation store on every route change. Placed once in AppLayout.
 *
 * Hydration rules:
 *   - Chemistry files page:  reads ?project= → sets projectId
 *   - Editor page:           reads file id from path, project from store/file data
 *   - Workbench pages:       reads project_id, file_id, molecule_id from query params
 *   - Top-level nav jumps:   clears previousPath when destination has no shared context
 */
const NavigationHydrator: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const hydrateFromSearchParams = useNavigationStore((s) => s.hydrateFromSearchParams);
  const clearPreviousNavigation = useNavigationStore((s) => s.clearPreviousNavigation);
  const previousPath = useNavigationStore((s) => s.previousPath);
  const prevPathnameRef = useRef(location.pathname);

  useEffect(() => {
    const { pathname } = location;

    // Hydrate context from URL params on every route change
    hydrateFromSearchParams(searchParams);

    // Clear previousPath when landing on any top-level page or leaving
    // the workbench section. The back link should only appear on workbench
    // tool pages when arrived from chemistry context.
    if (previousPath) {
      const TOP_LEVEL = ['/chemistry', '/biology', '/omics', '/genomics', '/pipeline', '/dashboard', '/admin', '/inventory', '/batches', '/spectra', '/files'];
      const isTopLevel = TOP_LEVEL.some(p => pathname === p || pathname === p + '/projects' || pathname === p + '/assays');
      const isWorkbench = pathname.startsWith('/hubs') || pathname.startsWith('/workbench');

      if (isTopLevel || (!isWorkbench && !pathname.startsWith('/chemistry'))) {
        clearPreviousNavigation();
      }
    }

    prevPathnameRef.current = pathname;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  return null;
};

export default NavigationHydrator;
